import React from 'react';
import { motion } from 'framer-motion';
import { Star, Zap, Shield, CreditCard, AlertTriangle } from 'lucide-react';
import { CTAButton } from '../ui/CTAButton';
export function Hero() {
  const scrollToOffer = () => {
    document.getElementById('offer')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <section className="relative w-full min-h-screen bg-gradient-to-b from-cream-bg to-cream-alt pt-20 pb-16 px-4 overflow-hidden" data-id="element-119">
      {/* Subtle gold glow background */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gold-accent/10 rounded-full blur-3xl pointer-events-none" data-id="element-120" />

      <div className="max-w-7xl mx-auto relative z-10" data-id="element-121">
        {/* Attention Banner */}
        <motion.div initial={{
        opacity: 0,
        y: -20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5
      }} className="flex justify-center mb-6" data-id="element-122">
          <div className="inline-flex items-center gap-2 bg-red-600 text-white px-5 py-2.5 rounded-full shadow-lg font-bold text-sm md:text-base" data-id="element-123">
            <AlertTriangle className="w-4 h-4 flex-shrink-0" data-id="element-124" />
            <span data-id="element-125">
              ความสนใจ: อาจารย์ฟุตบอลที่เหนื่อยกับการนั่งหาแบบฝึกหัดทั้งคืน
            </span>
          </div>
        </motion.div>

        {/* Main Headline */}
        <motion.h1 initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        delay: 0.2
      }} className="text-4xl md:text-5xl lg:text-6xl font-display font-bold text-center text-navy-primary leading-tight mb-6 max-w-5xl mx-auto" data-id="element-126">
          วางแผนการสอนทั้งฤดูกาล
          <br data-id="element-127" />
          ในเวลาเพียงไม่กี่นาที
        </motion.h1>

        {/* Subheadline */}
        <motion.p initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        delay: 0.4
      }} className="text-lg md:text-xl text-center text-gray-700 mb-12 max-w-4xl mx-auto leading-relaxed" data-id="element-128">
          ชุดการสอนฟุตบอล "พร้อมใช้งานทันที" ชุดแรก พร้อมอุปกรณ์ แบบฝึกหัด
          และแผนการฝึกที่เตรียมมาแล้ว ตั้งแต่กลุ่มเยาวชนไปจนถึงระดับสูง —
          คืนเวลาให้คุณ และยกระดับการสอนสู่ขั้นต่อไป
        </motion.p>

        {/* Product Mockup */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.8,
        delay: 0.6
      }} className="flex justify-center mb-6" data-id="element-129">
          <div className="relative" data-id="element-130">
            <img src="https://cdn.magicpatterns.com/uploads/buXD3KcbWgFp1MZw1bc7Qq/ChatGPT_Image_12_may_2026,_16_36_32-Photoroom.webp" alt="ฟุตบอล พร้อม - ระบบการสอนฟุตบอลสมบูรณ์แบบ" className="max-w-full h-auto w-full max-h-[500px] object-contain drop-shadow-2xl" data-id="element-131" />
          </div>
        </motion.div>

        {/* Inline Rating + Urgency (under mockup) */}
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        delay: 0.8
      }} className="flex flex-col items-center gap-3 mb-8" data-id="element-132">
          <div className="flex items-center gap-3 flex-wrap justify-center" data-id="element-133">
            <div className="flex gap-1" data-id="element-134">
              {[...Array(5)].map((_, i) => <Star key={i} className="w-6 h-6 fill-gold-accent text-gold-accent drop-shadow" data-id="element-135" />)}
            </div>
            <span className="text-lg md:text-xl font-semibold text-navy-primary" data-id="element-136">
              4.9/5{' '}
              <span className="text-gray-600 font-normal" data-id="element-137">
                (รีวิวที่ยืนยันแล้วกว่า 1,200 รายการ)
              </span>
            </span>
          </div>

          <div className="inline-flex items-center gap-2 bg-navy-primary text-white px-5 py-2 rounded-full shadow-md" data-id="element-138">
            <span className="text-base" data-id="element-139">🔥</span>
            <span className="text-sm md:text-base font-medium" data-id="element-140">
              อาจารย์ 67 ท่านเลือกระบบนี้วันนี้
            </span>
          </div>
        </motion.div>

        {/* CTA */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        delay: 1
      }} className="flex flex-col items-center gap-4 mb-8" data-id="element-141">
          <CTAButton onClick={scrollToOffer} size="large" className="w-full md:w-auto" data-id="element-142">
            ⚽ ใช่! ฉันต้องการระบบครบชุดนี้
          </CTAButton>
          <div className="flex flex-wrap justify-center gap-4 text-sm text-gray-600" data-id="element-143">
            <span className="flex items-center gap-1" data-id="element-144">
              <Zap className="w-4 h-4 text-gold-accent" data-id="element-145" />
              เข้าถึงได้ทันที
            </span>
            <span className="flex items-center gap-1" data-id="element-146">
              <Shield className="w-4 h-4 text-gold-accent" data-id="element-147" />
              รับประกัน 7 วัน
            </span>
            <span className="flex items-center gap-1" data-id="element-148">
              <CreditCard className="w-4 h-4 text-gold-accent" data-id="element-149" />
              ชำระครั้งเดียว
            </span>
          </div>
        </motion.div>

        {/* Trust Strip */}
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.6,
        delay: 1.2
      }} className="text-center" data-id="element-150">
          <p className="text-navy-primary font-medium text-lg" data-id="element-151">
            🇹🇭 แหล่งข้อมูลอันดับ 1 ที่อาจารย์ฟุตบอลไทยเลือกใช้
          </p>
        </motion.div>
      </div>
    </section>;
}